import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AlertTriangle, CheckCircle2, ClipboardList, RefreshCw, Play, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";

interface AuditEntry {
  id: string;
  booking_id: string;
  user_id: string;
  subscription_id: string;
  old_sessions_remaining: number | null;
  new_sessions_remaining: number | null;
  reason: string | null;
  triggered_by: string | null;
  action_time: string | null;
}

interface AuditEntryWithProfile extends AuditEntry {
  user_name?: string;
}

interface Discrepancy {
  subscription_id: string;
  user_id: string;
  gym_id: string;
  actual_remaining: number;
  expected_remaining: number;
  sessions_total: number;
  completed_bookings: number;
  late_cancels_without_joker: number;
  delta: number;
  user_name: string;
  gym_name: string;
  app_names: string[];
}

const fetchAuditLogs = async (): Promise<AuditEntryWithProfile[]> => {
  const { data, error } = await supabase
    .from("session_reduction_audit")
    .select("*")
    .order("action_time", { ascending: false })
    .limit(500);

  if (error) throw error;
  if (!data || data.length === 0) return [];

  const userIds = [...new Set(data.map((d) => d.user_id))];
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, first_name, last_name")
    .in("id", userIds);

  const profileMap = new Map(
    (profiles || []).map((p) => [p.id, `${p.first_name || ""} ${p.last_name || ""}`.trim()])
  );

  return data.map((entry) => ({
    ...entry,
    user_name: profileMap.get(entry.user_id) || entry.user_id.slice(0, 8),
  }));
};

function StatsCards({ logs }: { logs: AuditEntryWithProfile[] }) {
  const { t } = useTranslation(["superadmin2", "common"]);
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const correctionsThisMonth = logs.filter(
    (l) => l.reason === "consistency_check_correction" && l.action_time && new Date(l.action_time) >= startOfMonth
  ).length;

  const reductionsToday = logs.filter(
    (l) => l.action_time && new Date(l.action_time) >= startOfDay
  ).length;

  const anomalies = logs.filter(
    (l) =>
      (l.old_sessions_remaining !== null &&
        l.new_sessions_remaining !== null &&
        l.old_sessions_remaining === l.new_sessions_remaining) ||
      (l.new_sessions_remaining !== null && l.new_sessions_remaining < 0)
  ).length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t("sessionsAudit.correctionsThisMonth")}</CardTitle>
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{correctionsThisMonth}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t("sessionsAudit.reductionsToday")}</CardTitle>
          <ClipboardList className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{reductionsToday}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t("sessionsAudit.anomaliesDetected")}</CardTitle>
          <AlertTriangle className="h-4 w-4 text-destructive" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-destructive">{anomalies}</div>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditTable({
  logs,
  highlightDuplicates = false,
  emptyMessage,
  isMobile = false,
}: {
  logs: AuditEntryWithProfile[];
  highlightDuplicates?: boolean;
  emptyMessage?: string;
  isMobile?: boolean;
}) {
  const { t } = useTranslation(["superadmin2", "common"]);
  const resolvedEmptyMessage = emptyMessage ?? t("sessionsAudit.noLogs");
  const duplicateBookingIds = new Set<string>();
  if (highlightDuplicates) {
    const seen = new Map<string, string>();
    for (const log of logs) {
      const key = `${log.booking_id}-${log.old_sessions_remaining}-${log.new_sessions_remaining}`;
      if (seen.has(key)) {
        duplicateBookingIds.add(log.booking_id);
      }
      seen.set(key, log.id);
    }
  }

  if (logs.length === 0) {
    return <p className="text-muted-foreground text-center py-8">{resolvedEmptyMessage}</p>;
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("sessionsAudit.date")}</TableHead>
            <TableHead>{t("sessionsAudit.user")}</TableHead>
            {!isMobile && <TableHead>{t("sessionsAudit.subscription")}</TableHead>}
            <TableHead>{t("sessionsAudit.oldToNew")}</TableHead>
            <TableHead>{t("sessionsAudit.reason")}</TableHead>
            {!isMobile && <TableHead>{t("sessionsAudit.triggeredBy")}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const isDuplicate = duplicateBookingIds.has(log.booking_id);
            return (
              <TableRow key={log.id} className={isDuplicate ? "bg-destructive/10" : ""}>
                <TableCell className="whitespace-nowrap">
                  {log.action_time
                    ? format(new Date(log.action_time), "dd MMM yyyy HH:mm", { locale: fr })
                    : "—"}
                </TableCell>
                <TableCell>{log.user_name}</TableCell>
                {!isMobile && (
                  <TableCell className="font-mono text-xs">
                    {log.subscription_id.slice(0, 8)}…
                  </TableCell>
                )}
                <TableCell>
                  <span className="font-mono">
                    {log.old_sessions_remaining} → {log.new_sessions_remaining}
                  </span>
                  {isDuplicate && (
                    <Badge variant="destructive" className="ml-2 text-xs">{t("sessionsAudit.duplicate")}</Badge>
                  )}
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-xs">{log.reason || "—"}</TableCell>
                {!isMobile && (
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{log.triggered_by || "—"}</Badge>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function DiscrepancyTable({
  discrepancies,
  selectedIds,
  onToggle,
  onToggleAll,
  emptyMessage,
  isMobile = false,
}: {
  discrepancies: Discrepancy[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (checked: boolean) => void;
  emptyMessage?: string;
  isMobile?: boolean;
}) {
  const { t } = useTranslation(["superadmin2", "common"]);
  const resolvedEmptyMessage = emptyMessage ?? t("sessionsAudit.noDiscrepancies");
  if (discrepancies.length === 0) {
    return <p className="text-muted-foreground text-center py-8">{resolvedEmptyMessage}</p>;
  }

  const allSelected = discrepancies.every((d) => selectedIds.has(d.subscription_id));
  const someSelected = !allSelected && discrepancies.some((d) => selectedIds.has(d.subscription_id));

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={(c) => onToggleAll(!!c)}
                aria-label={t("sessionsAudit.selectAll")}
              />
            </TableHead>
            <TableHead>{t("sessionsAudit.member")}</TableHead>
            <TableHead>{t("sessionsAudit.app")}</TableHead>
            {!isMobile && <TableHead>{t("sessionsAudit.total")}</TableHead>}
            {!isMobile && <TableHead>{t("sessionsAudit.consumed")}</TableHead>}
            <TableHead>{t("sessionsAudit.current")}</TableHead>
            {!isMobile && <TableHead>{t("sessionsAudit.expected")}</TableHead>}
            <TableHead>{t("sessionsAudit.delta")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {discrepancies.map((d) => {
            const checked = selectedIds.has(d.subscription_id);
            return (
              <TableRow key={d.subscription_id} className={checked ? "bg-muted/30" : ""}>
                <TableCell>
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => onToggle(d.subscription_id)}
                    aria-label={t("sessionsAudit.selectUser", { name: d.user_name })}
                  />
                </TableCell>
                <TableCell className="font-medium">{d.user_name}</TableCell>
                <TableCell className="text-xs">
                  {d.app_names.length > 0 ? d.app_names.join(", ") : d.gym_name}
                </TableCell>
                {!isMobile && <TableCell>{d.sessions_total}</TableCell>}
                {!isMobile && <TableCell>{d.completed_bookings}</TableCell>}
                <TableCell className="font-mono">{d.actual_remaining}</TableCell>
                {!isMobile && <TableCell className="font-mono">{d.expected_remaining}</TableCell>}
                <TableCell>
                  <Badge variant={d.delta > 0 ? "default" : "destructive"} className="text-xs">
                    {d.delta > 0 ? "+" : ""}{d.delta}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function SessionsAudit() {
  const { t } = useTranslation(["superadmin2", "common"]);
  const isMobile = useIsMobile();
  const [searchFilter, setSearchFilter] = useState("");
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [auditRunning, setAuditRunning] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [showCorrectDialog, setShowCorrectDialog] = useState(false);

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(discrepancies.map((d) => d.subscription_id)) : new Set());
  };

  const { data: logs = [], isLoading, refetch } = useQuery({
    queryKey: ["sessions-audit-logs"],
    queryFn: fetchAuditLogs,
  });

  const corrections = logs.filter((l) => l.reason === "consistency_check_correction");
  const anomalies = logs.filter(
    (l) =>
      (l.old_sessions_remaining !== null &&
        l.new_sessions_remaining !== null &&
        l.old_sessions_remaining === l.new_sessions_remaining) ||
      (l.new_sessions_remaining !== null && l.new_sessions_remaining < 0)
  );

  const filteredLogs = searchFilter
    ? logs.filter(
        (l) =>
          l.user_name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          l.triggered_by?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          l.reason?.toLowerCase().includes(searchFilter.toLowerCase())
      )
    : logs;

  const runAudit = async (autoCorrect: boolean) => {
    if (autoCorrect) setCorrecting(true);
    else setAuditRunning(true);

    try {
      const body: Record<string, unknown> = { auto_correct: autoCorrect };
      if (autoCorrect) body.subscription_ids = Array.from(selectedIds);

      const { data, error } = await supabase.functions.invoke('audit-sessions-consistency', { body });

      if (error) {
        toast.error(t("sessionsAudit.errorGeneric", { message: error.message || t("sessionsAudit.errorUnknown") }));
        return;
      }

      // L'edge function peut répondre 200 avec { error }. Sans ce test, on
      // affichait « undefined corrections appliquées ».
      if (!data || (data as any).error) {
        toast.error(t("sessionsAudit.errorGeneric", { message: (data as any)?.error || t("sessionsAudit.errorUnexpected") }));
        return;
      }

      if (autoCorrect) {
        toast.success(t("sessionsAudit.correctionsApplied", { count: data.corrections_count }));
        // Remove corrected from list
        const correctedIds = new Set((data.corrections || []).map((c: Discrepancy) => c.subscription_id));
        setDiscrepancies((prev) => prev.filter((d) => !correctedIds.has(d.subscription_id)));
        setSelectedIds(new Set());
        refetch();
      } else {
        const list: Discrepancy[] = data.discrepancies || [];
        setDiscrepancies(list);
        setSelectedIds(new Set(list.map((d) => d.subscription_id)));
        toast.info(t("sessionsAudit.discrepanciesDetected", { count: data.discrepancies_count, checked: data.checked }));
      }
    } catch (err) {
      toast.error(t("sessionsAudit.auditCallError"));
      console.error(err);
    } finally {
      setAuditRunning(false);
      setCorrecting(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="p-3 md:p-6 overflow-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
          <h1 className="text-xl md:text-2xl font-bold">{t("sessionsAudit.title")}</h1>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => runAudit(false)}
              disabled={auditRunning}
              className="flex-1 md:flex-none"
            >
              <Play className="h-4 w-4 mr-2" />
              {auditRunning ? t("sessionsAudit.auditRunning") : isMobile ? t("sessionsAudit.dryRunShort") : t("sessionsAudit.dryRunFull")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowCorrectDialog(true)}
              disabled={correcting || selectedIds.size === 0}
              className="flex-1 md:flex-none"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {correcting ? t("sessionsAudit.correcting") : t("sessionsAudit.correct", { selected: selectedIds.size, total: discrepancies.length })}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {!isMobile && t("sessionsAudit.refresh")}
            </Button>
          </div>
        </div>

        <AlertDialog open={showCorrectDialog} onOpenChange={setShowCorrectDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("sessionsAudit.confirmCorrectionsTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("sessionsAudit.confirmCorrectionsDesc", { selected: selectedIds.size, total: discrepancies.length })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("sessionsAudit.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowCorrectDialog(false);
                  runAudit(true);
                }}
              >
                {t("sessionsAudit.apply")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {isLoading ? (
          <p className="text-muted-foreground">{t("sessionsAudit.loading")}</p>
        ) : (
          <>
            <StatsCards logs={logs} />

            <Tabs defaultValue="discrepancies" className="w-full">
              <TabsList className="w-full justify-start overflow-x-auto">
                <TabsTrigger value="discrepancies" className="text-xs md:text-sm">
                  {t("sessionsAudit.discrepancies", { count: discrepancies.length })}
                </TabsTrigger>
                <TabsTrigger value="corrections" className="text-xs md:text-sm">
                  {t("sessionsAudit.corrections", { count: corrections.length })}
                </TabsTrigger>
                <TabsTrigger value="all" className="text-xs md:text-sm">
                  {t("sessionsAudit.all", { count: logs.length })}
                </TabsTrigger>
                <TabsTrigger value="errors" className="text-xs md:text-sm">
                  {t("sessionsAudit.errors", { count: anomalies.length })}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="discrepancies">
                <DiscrepancyTable
                  discrepancies={discrepancies}
                  selectedIds={selectedIds}
                  onToggle={toggleOne}
                  onToggleAll={toggleAll}
                  emptyMessage={t("sessionsAudit.runAuditPrompt")}
                  isMobile={isMobile}
                />
              </TabsContent>

              <TabsContent value="corrections">
                <AuditTable
                  logs={corrections}
                  emptyMessage={t("sessionsAudit.noAutoCorrections")}
                  isMobile={isMobile}
                />
              </TabsContent>

              <TabsContent value="all">
                <div className="mb-4">
                  <Input
                    placeholder={t("sessionsAudit.filterPlaceholder")}
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                <AuditTable
                  logs={filteredLogs}
                  highlightDuplicates
                  emptyMessage={t("sessionsAudit.noLogsFound")}
                  isMobile={isMobile}
                />
              </TabsContent>

              <TabsContent value="errors">
                <AuditTable
                  logs={anomalies}
                  emptyMessage={t("sessionsAudit.noAnomalies")}
                  isMobile={isMobile}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}
